import { DayOfWeek, LocalDate, LocalTime } from '@js-joda/core';
import { compact } from 'lodash';

import {
   AppointmentEncounterMode,
   AvailabilityTypeSimple,
   BookingChannelParameters,
} from '@dreryk/egabinet-appointment-frontend-api';

import { TranslatableText } from '@dreryk/react-common';
import { RadioGroupItem } from '@dreryk/react-components';

import { getDocplannerAvailabilityLabel, getPatientPortalAvailabilityLabel } from '~libs.shared.appointments';

import { personnelTranslationKeys } from '../../translations';
import { AvailabilityUpdateFormBean } from '../availabilityUpdate/availabilityUpdate.types';
import { AvailabilityCreationFormBean } from '../availabilityCreation/availabilityCreation.types';

import { EncounterModeValue } from './fields/EncounterMode/EncounterModeCheckboxes.types';
import { FormType, RecurrencePeriod, RecurrencePeriodUnion } from './common.types';

export const TIME_STEP_IN_MINUTES = 5;
export const MIN_TIME = LocalTime.MIN;
export const MAX_TIME = LocalTime.parse('23:55');
export const WEEK_START_DAY = DayOfWeek.MONDAY;

export const recurrenceTypes: RadioGroupItem<RecurrencePeriodUnion>[] = [
   {
      id: RecurrencePeriod.NONE,
      label: personnelTranslationKeys.doNotRepeat,
   },
   {
      id: RecurrencePeriod.EVERY_WEEK,
      label: personnelTranslationKeys.everyWeek,
   },
   {
      id: RecurrencePeriod.EVERY_TWO_WEEKS,
      label: personnelTranslationKeys.everyTwoWeeks,
   },
];

export const findDefaultAvailabilityTypeId = (
   availabilityTypes?: AvailabilityTypeSimple[]
): AvailabilityTypeSimple | undefined => {
   return (availabilityTypes ?? []).find((type) => type.defaultType);
};

export const validateAvailabilityDate = (
   data: unknown,
   getValue: (name: string) => unknown,
   formType: FormType
): TranslatableText | undefined => {
   if (data instanceof LocalDate) {
      const dateIsBeforeCurrentDay = data.isBefore(LocalDate.now());
      const dateIsBeforeCurrentWeek = data.isBefore(LocalDate.now().with(WEEK_START_DAY));
      const recurring = getValue('recurrencePeriod') !== RecurrencePeriod.NONE;

      if (!recurring && dateIsBeforeCurrentDay) {
         return formType === FormType.CREATION
            ? personnelTranslationKeys.incorrectPastDateForSingleAvailability
            : personnelTranslationKeys.incorrectPastDateForSingleAvailabilityUpdate;
      }
      if (recurring && dateIsBeforeCurrentWeek) {
         return formType === FormType.CREATION
            ? personnelTranslationKeys.incorrectPastDateForRecurringAvailability
            : personnelTranslationKeys.incorrectPastDateForRecurringAvailabilityUpdate;
      }
   }
   return undefined;
};

export const validateAvailabilityEndTime = (
   data: LocalTime,
   getValue: (name: string) => unknown
): TranslatableText | undefined => {
   if (!data.isAfter(getValue('startTime') as LocalTime)) {
      return personnelTranslationKeys.incorrectScheduleTimeRange;
   }
   return undefined;
};

export const castToLocalTime = (value: unknown): LocalTime => {
   return value as LocalTime;
};

export const triggerLabelExtractor = (currentValue?: AvailabilityTypeSimple): string => currentValue?.name ?? '';

export const isEndOfDay = (max: LocalTime): boolean => {
   return LocalTime.MIDNIGHT.equals(max);
};

type BookingChannelConfig = { enabled: boolean; mode: EncounterModeValue | undefined; label: string };

const buildEncounterModes = (mode: EncounterModeValue | undefined): AppointmentEncounterMode[] =>
   compact([mode?.onSite && 'ON_SITE', mode?.videoConsultation && 'VIDEO_CONSULTATION']);

export const getBookingChannels = (
   parameters: AvailabilityCreationFormBean | AvailabilityUpdateFormBean
): BookingChannelParameters[] => {
   const configs: BookingChannelConfig[] = [
      {
         enabled: Boolean(parameters.sendToDocplanner),
         mode: parameters.docplannerEncounterMode,
         label: getDocplannerAvailabilityLabel(),
      },
      {
         enabled: Boolean(parameters.sendToPatientPortal),
         mode: parameters.patientPortalEncounterMode,
         label: getPatientPortalAvailabilityLabel(),
      },
   ];

   return configs
      .filter((bookingChannelConfig) => bookingChannelConfig.enabled)
      .map(({ mode, label }) => ({ encounterModes: buildEncounterModes(mode), label: label }));
};
